<script setup lang="ts">
import {
  ChevronRight,
  MoreVertical,
  SquarePlus,
  TriangleAlert,
} from "@lucide/vue";
import { onClickOutside } from "@vueuse/core";
import { computed, onMounted, ref } from "vue";
import type { Block } from "@aswin.dev/types";

import { useI18n } from "../composables/useI18n";
import { EDITOR_KEY, requireInject } from "../keys";

const props = defineProps<{
  previewMode: boolean;
  multiPageCanvas: boolean;
}>();

const { t } = useI18n();
const editor = requireInject(EDITOR_KEY, "CanvasPagesNav");

const canvasPagesStrip = computed(() => editor.content.value.canvasPages ?? []);

const activeCanvasPageId = computed(
  () => editor.content.value.activeCanvasPageId ?? null,
);

const stepStripRef = ref<HTMLElement | null>(null);
const openStepMenuPageId = ref<string | null>(null);
const hoveredUnreachablePageId = ref<string | null>(null);
const tooltipPos = ref<{ left: number; top: number } | null>(null);

function showUnreachableTooltip(pageId: string, ev: Event): void {
  const target = ev.currentTarget as HTMLElement | null;
  if (!target) return;
  const rect = target.getBoundingClientRect();
  hoveredUnreachablePageId.value = pageId;
  tooltipPos.value = {
    left: rect.left + rect.width / 2,
    top: rect.top,
  };
}

function hideUnreachableTooltip(pageId: string): void {
  if (hoveredUnreachablePageId.value === pageId) {
    hoveredUnreachablePageId.value = null;
    tooltipPos.value = null;
  }
}

function hasNextStepButton(blocks: Block[]): boolean {
  for (const block of blocks) {
    if (block.type === "button" && block.clickAction === "next_step") {
      return true;
    }
    if (block.type === "form" && block.buttonAction === "next_step") {
      return true;
    }
    if (block.type === "section") {
      for (const column of block.children) {
        if (hasNextStepButton(column)) {
          return true;
        }
      }
    }
  }
  return false;
}

const unreachablePageIds = computed<Set<string>>(() => {
  const pages = canvasPagesStrip.value;
  const result = new Set<string>();
  for (let i = 1; i < pages.length; i += 1) {
    if (!hasNextStepButton(pages[i - 1].blocks)) {
      result.add(pages[i].id);
    }
  }
  return result;
});

onClickOutside(stepStripRef, () => {
  openStepMenuPageId.value = null;
});

onMounted(() => {
  if (props.multiPageCanvas) {
    editor.ensureCanvasPages();
  }
});

const show = computed(
  () => props.multiPageCanvas && canvasPagesStrip.value.length > 0,
);

function selectCanvasPage(pageId: string): void {
  if (pageId === activeCanvasPageId.value) {
    return;
  }
  openStepMenuPageId.value = null;
  editor.switchCanvasPage(pageId);
}

function toggleStepMenu(pageId: string): void {
  openStepMenuPageId.value =
    openStepMenuPageId.value === pageId ? null : pageId;
}

function handleAddCanvasPage(): void {
  openStepMenuPageId.value = null;
  editor.addCanvasPage();
}

function handleRenameCanvasPage(pageId: string, currentTitle: string): void {
  const next = window.prompt(t.canvas.pages.renamePrompt, currentTitle);
  if (next === null) {
    return;
  }
  editor.renameCanvasPage(pageId, next);
  openStepMenuPageId.value = null;
}

function handleRemoveCanvasPage(pageId: string): void {
  if (canvasPagesStrip.value.length <= 1) {
    return;
  }
  editor.removeCanvasPage(pageId);
  openStepMenuPageId.value = null;
}
</script>

<template>
  <nav
    v-if="show"
    ref="stepStripRef"
    class="tpl-canvas-steps-nav tpl:flex tpl:w-full tpl:flex-wrap tpl:items-center tpl:gap-1 tpl:rounded-lg tpl:border tpl:border-[var(--tpl-border)] tpl:bg-[var(--tpl-bg)] tpl:px-3 tpl:py-2.5 tpl:shadow-[var(--tpl-shadow-md)] tpl:font-[var(--tpl-font-family)]"
    role="tablist"
    :aria-label="t.canvas.pages.ariaStrip"
  >
    <template v-for="(page, idx) in canvasPagesStrip" :key="page.id">
      <ChevronRight
        v-if="idx > 0"
        :size="16"
        :stroke-width="2"
        class="tpl:pointer-events-none tpl:mx-0.5 tpl:text-[var(--tpl-text-muted)]"
        aria-hidden="true"
      />
      <div class="tpl:relative tpl:inline-flex tpl:items-stretch">
        <div
          class="tpl:inline-flex tpl:items-center tpl:overflow-hidden tpl:rounded-full tpl:border tpl:text-sm tpl:font-semibold tpl:transition-colors tpl:duration-150"
          :class="
            activeCanvasPageId === page.id
              ? 'tpl-canvas-step tpl-canvas-step--active'
              : 'tpl-canvas-step'
          "
        >
          <button
            type="button"
            role="tab"
            class="tpl:max-w-[200px] tpl:truncate tpl:border-none tpl:bg-transparent tpl:px-3 tpl:py-1.5 tpl:text-left tpl:text-[var(--tpl-text)] tpl:outline-none tpl:transition-colors tpl:duration-150 focus-visible:tpl-ring-2 focus-visible:tpl-ring-[var(--tpl-primary)] focus-visible:tpl-ring-offset-2"
            :aria-selected="activeCanvasPageId === page.id"
            :tabindex="activeCanvasPageId === page.id ? 0 : -1"
            @click="selectCanvasPage(page.id)"
          >
            {{ page.title }}
          </button>
          <span
            v-if="unreachablePageIds.has(page.id)"
            class="tpl-canvas-step-warning tpl:flex tpl:shrink-0 tpl:cursor-help tpl:items-center tpl:justify-center tpl:border-0 tpl:border-l tpl:border-solid tpl:border-[var(--tpl-border)] tpl:px-2 tpl:outline-none"
            tabindex="0"
            role="img"
            :aria-label="t.canvas.pages.unreachableLabel"
            @mouseenter="showUnreachableTooltip(page.id, $event)"
            @mouseleave="hideUnreachableTooltip(page.id)"
            @focus="showUnreachableTooltip(page.id, $event)"
            @blur="hideUnreachableTooltip(page.id)"
          >
            <TriangleAlert :size="16" :stroke-width="2" />
          </span>
          <button
            v-if="!previewMode"
            type="button"
            class="tpl:flex tpl:shrink-0 tpl:cursor-pointer tpl:items-center tpl:justify-center tpl:border-0 tpl:border-l tpl:border-solid tpl:border-[var(--tpl-border)] tpl:bg-transparent tpl:px-2 tpl:text-[var(--tpl-text-muted)] tpl:outline-none tpl:transition-colors tpl:duration-150 hover:tpl:text-[var(--tpl-text)] focus-visible:tpl-ring-2 focus-visible:tpl-ring-inset focus-visible:tpl-ring-[var(--tpl-primary)]"
            :aria-expanded="openStepMenuPageId === page.id"
            :aria-haspopup="true"
            :aria-label="`${page.title}: ${t.canvas.pages.stepActions}`"
            @click.stop="toggleStepMenu(page.id)"
          >
            <MoreVertical :size="16" :stroke-width="2" />
          </button>
        </div>
        <div
          v-if="openStepMenuPageId === page.id && !previewMode"
          class="tpl:absolute tpl:top-full tpl:right-0 tpl:z-[60] tpl:mt-1 tpl:min-w-[10rem] tpl:rounded-lg tpl:border tpl:border-[var(--tpl-border)] tpl:bg-[var(--tpl-bg)] tpl:py-1 tpl:shadow-lg"
          role="menu"
        >
          <button
            type="button"
            role="menuitem"
            class="tpl:flex tpl:w-full tpl:cursor-pointer tpl:items-center tpl:border-none tpl:bg-transparent tpl:px-3 tpl:py-2 tpl:text-left tpl:text-sm tpl:text-[var(--tpl-text)] tpl:outline-none hover:tpl:bg-[var(--tpl-bg-elevated)]"
            @click="handleRenameCanvasPage(page.id, page.title)"
          >
            {{ t.canvas.pages.rename }}
          </button>
          <button
            v-if="canvasPagesStrip.length > 1"
            type="button"
            role="menuitem"
            class="tpl:flex tpl:w-full tpl:cursor-pointer tpl:items-center tpl:border-none tpl:bg-transparent tpl:px-3 tpl:py-2 tpl:text-left tpl:text-sm tpl:text-[var(--tpl-danger)] tpl:outline-none hover:tpl:bg-[var(--tpl-bg-elevated)]"
            @click="handleRemoveCanvasPage(page.id)"
          >
            {{ t.canvas.pages.delete }}
          </button>
        </div>
      </div>
    </template>
    <button
      v-if="!previewMode"
      type="button"
      class="tpl:ml-1 tpl:inline-flex tpl:cursor-pointer tpl:items-center tpl:gap-1 tpl:rounded-full tpl:border tpl:border-dashed tpl:border-[var(--tpl-border)] tpl:bg-transparent tpl:px-3 tpl:py-1.5 tpl:text-xs tpl:font-semibold tpl:text-[var(--tpl-primary)] tpl:outline-none tpl:transition-colors tpl:duration-150 hover:tpl:border-[var(--tpl-primary)] hover:tpl:bg-[var(--tpl-primary-light)] focus-visible:tpl-ring-2 focus-visible:tpl-ring-[var(--tpl-primary)]"
      @click="handleAddCanvasPage"
    >
      <SquarePlus :size="14" :stroke-width="2" />
      {{ t.canvas.pages.addStep }}
    </button>
  </nav>
  <Teleport to="body">
    <div
      v-if="hoveredUnreachablePageId && tooltipPos"
      role="tooltip"
      class="tpl-canvas-step-warning-tooltip"
      :style="{
        left: `${tooltipPos.left}px`,
        top: `${tooltipPos.top}px`,
      }"
    >
      {{ t.canvas.pages.unreachableTooltip }}
    </div>
  </Teleport>
</template>

<style scoped>
.tpl-canvas-step {
  border-color: var(--tpl-border);
  background-color: color-mix(in srgb, var(--tpl-bg) 92%, transparent);
}

.tpl-canvas-step--active {
  border-color: color-mix(in srgb, var(--tpl-text) 55%, var(--tpl-border));
  background-color: var(--tpl-bg);
  box-shadow: 0 0 0 1px color-mix(in srgb, var(--tpl-text) 12%, transparent);
}

.tpl-canvas-step-warning {
  color: #d97706;
}

.tpl-canvas-step-warning:focus-visible {
  outline: 2px solid var(--tpl-primary);
  outline-offset: -2px;
  border-radius: 9999px;
}
</style>

<style>
.tpl-canvas-step-warning-tooltip {
  position: fixed;
  z-index: 99999;
  width: 16rem;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  background-color: #1f2937;
  color: #ffffff;
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.5;
  pointer-events: none;
  white-space: normal;
  transform: translate(-50%, calc(-100% - 8px));
  box-shadow:
    0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -4px rgba(0, 0, 0, 0.1);
}

.tpl-canvas-step-warning-tooltip::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 6px solid transparent;
  border-top-color: #1f2937;
}
</style>
